import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

function getExcerpt(htmlString, limit = 100) {
  // Remove HTML tags
  const textOnly = htmlString.replace(/<[^>]+>/g, '');
  if (textOnly.length <= limit) {
    return textOnly;
  }
  return textOnly.slice(0, limit) + '...';
}

function BlogPage({ darkMode, posts, editPost, deletePost, setActivePage, formatDate, isAdmin, setSelectedPost }) {
  const handleSeeMore = (post) => {
    setSelectedPost(post);
    setActivePage('blog-details');
  };

  const themeClasses = {
    background: darkMode ? 'bg-gray-900' : 'bg-gray-50',
    card: darkMode ? 'bg-gray-800' : 'bg-white',
    text: darkMode ? 'text-gray-100' : 'text-gray-800',
    subtext: darkMode ? 'text-gray-400' : 'text-gray-600',
    accent: darkMode ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700' : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600',
    accentText: 'text-white',
    tag: darkMode ? 'bg-indigo-900/70 text-indigo-300 border border-indigo-700' : 'bg-indigo-100 text-indigo-800 border border-indigo-200',
    editBtn: darkMode ? 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700' : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600',
    deleteBtn: darkMode ? 'bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700' : 'bg-gradient-to-r from-rose-500 to-red-500 hover:from-rose-600 hover:to-red-600',
    shadow: 'shadow-xl hover:shadow-2xl transition-all duration-300'
  };

  return (
    <div className={`${themeClasses.background} min-h-screen py-12 px-4 sm:px-6`}>
      <div className="max-w-6xl mx-auto">
        <h2 className={`text-4xl md:text-5xl font-extrabold mb-12 ${themeClasses.text} tracking-tight fade-in-down relative`}>
          <span className="relative inline-block pb-3">
            Blog Posts
            <span className="absolute bottom-0 left-0 w-full h-1.5 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full"></span>
          </span>
        </h2>
        
        {posts.length > 0 ? (
          <div className="grid grid-cols-1 gap-10">
            {posts.map((post, index) => (
              <article
                key={post.id}
                className={`p-8 sm:p-10 rounded-2xl ${themeClasses.card} ${themeClasses.shadow} overflow-hidden hover-lift border fade-in-up ${darkMode ? 'border-gray-700/50 hover:border-purple-500/50' : 'border-gray-100 hover:border-indigo-200'}`}
                style={{animationDelay: `${index * 0.1}s`}}
              >
                <div className="flex flex-col md:flex-row justify-between md:items-start gap-6 mb-8">
                  <div className="flex-1">
                    <h3 className={`text-3xl sm:text-4xl font-bold ${themeClasses.text} mb-4 hover:text-gradient transition-all duration-300`}>{post.title}</h3>
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <p className={`text-sm font-medium ${themeClasses.subtext}`}>
                        {formatDate(post.date)}
                        {post.edited && ` • Edited: ${formatDate(post.edited)}`}
                      </p>
                      <span className="inline-block w-2 h-2 rounded-full bg-gradient-to-r from-indigo-400 to-purple-400"></span>
                      <span
                        className={`inline-block px-4 py-1.5 text-sm font-semibold rounded-full shadow-sm ${themeClasses.tag}`}
                      >
                        {post.category}
                      </span>
                    </div>
                  </div>
                  
                  {isAdmin && (
                    <div className="flex space-x-3">
                      <button
                        onClick={() => editPost(post.id)}
                        className={`px-4 py-2.5 rounded-lg ${themeClasses.editBtn} text-white font-semibold flex items-center shadow-lg transition-all duration-300 hover:scale-105`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                      <button
                        onClick={() => deletePost(post.id)}
                        className={`px-4 py-2.5 rounded-lg ${themeClasses.deleteBtn} text-white font-semibold flex items-center shadow-lg transition-all duration-300 hover:scale-105`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    </div>
                  )}
                </div>

                <div
                  className={`mt-6 ${themeClasses.text} prose prose-lg max-w-none leading-relaxed`}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    padding: 0,
                    color: 'inherit',
                  }}
                >
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {getExcerpt(post.content, 250)}
                  </ReactMarkdown>
                </div>

                <div className="mt-8">
                  <button
                    onClick={() => handleSeeMore(post)}
                    className={`px-6 py-3 rounded-xl ${themeClasses.accent} ${themeClasses.accentText} font-semibold transition-all duration-300 shadow-lg hover:scale-105 hover-lift inline-flex items-center group`}
                  >
                    Read Full Article
                    <svg className="ml-2 w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                    </svg>
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className={`p-16 text-center rounded-2xl ${themeClasses.card} shadow-2xl border fade-in-up ${darkMode ? 'border-gray-700/50' : 'border-gray-100'}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-24 w-24 mx-auto mb-6 text-indigo-400 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
            <p className={`text-3xl font-bold mb-4 ${themeClasses.text}`}>No blog posts yet.</p>
            <p className={`text-lg mb-8 ${themeClasses.subtext}`}>Start sharing your thoughts and ideas with the world!</p>
            {isAdmin && (
              <button
                onClick={() => setActivePage('new-post')}
                className={`px-8 py-4 rounded-xl ${themeClasses.accent} ${themeClasses.accentText} font-semibold transition-all duration-300 shadow-xl hover:scale-105 hover-lift`}
              >
                Create Your First Post
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default BlogPage;